import {
  AgentCustomSqlTestConfigChange,
  getAgentTestChangeRowId,
  isAgentCustomSqlTestCreateChange,
} from '@elementary/common-ui/types';
import {
  DEFAULT_CUSTOM_SQL_CLOUD_SCHEDULE,
  getCustomSqlScheduleFromChange,
  resolveCustomSqlDestination,
} from '@elementary/common-ui/utils/customSqlTestConfig';
import { DEFAULT_CUSTOM_SQL_CONFIG } from 'shared/components/AddTestWizard/constants';
import {
  buildCustomSqlConfig,
  buildCloudSqlTestSchedule,
  cloudSqlTestScheduleToForm,
  customSqlSeverityToForm,
} from 'shared/components/AddTestWizard/customSqlCloudTest';
import { CustomSqlPlanWizardState } from 'shared/components/AddTestWizard/types';
import { AddTestForm } from 'shared/types';
import {
  DirtyFields,
  Mapper,
  PlanFieldDescriptor,
  changeToForm,
  extractNew,
  formToChange,
  isPathDirty,
  listMapper,
  makeSeverityMapper,
  nullableMapper,
  setFieldNew,
} from './planFieldMappers';

export const DEFAULT_CUSTOM_SQL_TEST_PATH = 'tests';

const pathMapper: Mapper = {
  toForm: (planValue) =>
    (planValue as string | null) ?? DEFAULT_CUSTOM_SQL_TEST_PATH,
  toPlan: (formValue) => (formValue as string | null) ?? null,
};

const CUSTOM_SQL_DESCRIPTORS: PlanFieldDescriptor[] = [
  { planKey: 'path', formPath: 'path', mapper: pathMapper },
  { planKey: 'description', formPath: 'description', mapper: nullableMapper },
  {
    planKey: 'severity',
    formPath: 'severity',
    mapper: makeSeverityMapper('lower'),
  },
  { planKey: 'tags', formPath: 'tags', mapper: listMapper },
  { planKey: 'owners', formPath: 'owners', mapper: listMapper },
  {
    planKey: 'qualityDimension',
    formPath: 'qualityDimension',
    mapper: nullableMapper,
  },
];

export const buildCustomSqlPlanWizardState = (
  change: AgentCustomSqlTestConfigChange,
  isCloudEnabled: boolean,
  planEnvId?: string,
): CustomSqlPlanWizardState => {
  const jinjaQuery = extractNew(change.sqlQuery) ?? '';
  const rawQuery = extractNew(change.rawSqlQuery) ?? '';
  const isCreate = isAgentCustomSqlTestCreateChange(change);
  const destination = resolveCustomSqlDestination(change, isCloudEnabled);
  const schedule =
    (isCreate ? getCustomSqlScheduleFromChange(change) : null) ??
    DEFAULT_CUSTOM_SQL_CLOUD_SCHEDULE;
  const config = {
    ...DEFAULT_CUSTOM_SQL_CONFIG,
    ...(isCreate ? change.customSqlConfig : undefined),
  };
  const severityValue = extractNew(change.severity);

  return {
    planSource: 'custom-sql-plan',
    planRowId: getAgentTestChangeRowId(change),
    planEnvId,
    operation: 'create',
    wizardResourceType: 'customQuery',
    initialValues: {
      ...changeToForm(change, CUSTOM_SQL_DESCRIPTORS, {
        testType: 'customQuery',
        testName: isCreate ? change.name : '',
        customQuery: rawQuery || jinjaQuery,
        customQueryConverted: jinjaQuery,
        customQueryCodeType: rawQuery ? 'sql' : 'jinja',
        testedTable: isCreate ? change.overridePrimaryTestModelId : undefined,
        customQueryDestination: destination,
        ...cloudSqlTestScheduleToForm(schedule),
        storeRowSample: config.store_row_sample,
        sampleRowLimit: config.sample_row_limit,
        queryTimeoutSeconds: config.query_timeout_seconds,
        customSqlPrimaryAssetId: isCreate
          ? change.primaryAssetId ?? change.assetId ?? null
          : null,
      }),
      severity: customSqlSeverityToForm(
        severityValue ?? 'error',
        config.fail_row_count_threshold,
        config.warn_row_count_threshold,
      ),
    },
  };
};

export const applyFormToCustomSqlChange = (
  change: AgentCustomSqlTestConfigChange,
  form: AddTestForm,
  dirtyFields: DirtyFields,
): AgentCustomSqlTestConfigChange => {
  let next = formToChange(change, form, dirtyFields, CUSTOM_SQL_DESCRIPTORS);

  // Validating an edited query sets customQueryConverted without marking it
  // dirty, so the pair is keyed off the raw SQL the user actually edited.
  if (isPathDirty(dirtyFields, 'customQuery')) {
    next = {
      ...next,
      rawSqlQuery: setFieldNew(next.rawSqlQuery, form.customQuery ?? null),
      sqlQuery: setFieldNew(next.sqlQuery, form.customQueryConverted ?? null),
    };
  }

  if (!isAgentCustomSqlTestCreateChange(next)) return next;

  if (isPathDirty(dirtyFields, 'testName') && form.testName?.trim()) {
    next = { ...next, name: form.testName.trim() };
  }
  if (isPathDirty(dirtyFields, 'testedTable')) {
    next = { ...next, overridePrimaryTestModelId: form.testedTable };
  }

  if (
    form.customQueryDestination === 'cloud' ||
    form.customQueryDestination === 'pr'
  ) {
    next = { ...next, destination: form.customQueryDestination };
    if (form.customQueryDestination === 'cloud') {
      next = {
        ...next,
        customSqlSchedule: buildCloudSqlTestSchedule(form),
        customSqlConfig: buildCustomSqlConfig(form),
        primaryAssetId: form.customSqlPrimaryAssetId ?? next.assetId,
      };
    }
  }

  return next;
};
